import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { window } from '../../../../globals';
import { getScimStatusQueryKey, useScimStatus } from '../../../../hooks/api/organization/useScimStatus';
import Alert, { ALERT_KIND } from '../../../generic/Alert';
import Button, { BUTTON_TYPE } from '../../../generic/Button';
import CopyToClipboard from '../../../generic/CopyToClipboard';
import { ErrorAlert } from '../../../generic/ErrorAlert';
import Flex from '../../../generic/Flex';
import { TextInput } from '../../../generic/Forms';
import Loader from '../../../generic/Loader';
import Text from '../../../generic/Text';
import Header from '../../../shared/settings/SettingsPageHeader';

import { useGenerateScimToken, useRevokeScimToken } from './hooks/useScimToken';
import SupportedAttributes from './SupportedAttributes';

import styles from './ScimProvisioning.module.less';

const SCIM_BASE_PATH = '/api/scim';

/**
 * Lets an org admin mint or revoke the bearer token an identity provider (e.g. Okta, EntraID) uses to
 * authenticate SCIM requests, and reports whether provisioning is live. "Enabled" means an active token
 * exists — the plaintext is only ever shown once, immediately after it is generated, so the page tracks the
 * token's presence rather than its value.
 */
const ScimProvisioning: VoidFunctionComponent = () => {
  const organizationId = window.user?.organizationId as number;
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);
  const scimBaseUrl = `${window.location.origin}${SCIM_BASE_PATH}`;
  const queryClient = useQueryClient();

  const { data: status, isLoading, isError, refetch } = useScimStatus(organizationId);

  // Generating or revoking flips `isEnabled`, which the server derives from the org's token rows.
  const invalidateStatus = () => queryClient.invalidateQueries({ queryKey: getScimStatusQueryKey(organizationId) });

  const { mutate: generateToken, isPending: isGenerating } = useGenerateScimToken(organizationId, {
    onSuccess: (response) => {
      setGeneratedToken(response?.token ?? null);
      invalidateStatus();
    },
  });
  const { mutate: revokeToken, isPending: isRevoking } = useRevokeScimToken(organizationId, {
    onSuccess: () => {
      setGeneratedToken(null);
      invalidateStatus();
    },
  });
  // Regenerate and revoke mutate the same bearer-token state, so a late one could undo the other; lock
  // both controls whenever either request is in flight.
  const isTokenMutationPending = isGenerating || isRevoking;

  if (isLoading) {
    return <Loader />;
  }

  const pageHeader = (
    <Header heading="SCIM Provisioning">
      <Header.Paragraph>
        Automatically create, update, and deactivate FOSSA users from your identity provider using SCIM.
      </Header.Paragraph>
    </Header>
  );

  // A failed status request leaves `status` undefined; surface the failure with a retry rather than
  // falling through to the "configure SSO" copy, which would misrepresent an outage as a setup step.
  if (isError || !status) {
    return (
      <>
        {pageHeader}
        <ErrorAlert
          status={{ errors: [new Error('Something went wrong while loading SCIM provisioning status. Please try again.')] }}
          actionButtonText="Retry"
          onAction={() => refetch()}
          withMargin
        />
      </>
    );
  }

  const isSsoConfigured = status.isSsoConfigured;
  const isEnabled = status.isEnabled;

  return (
    <>
      {pageHeader}

      {!isSsoConfigured && (
        <Alert kind={ALERT_KIND.INFO} header="Configure SSO before enabling SCIM">
          Users created over SCIM can only sign in through your identity provider, so they would have no way to
          reach FOSSA until single sign-on is configured. Set up SSO under Authentication, then return here to
          generate a token.
        </Alert>
      )}

      {isSsoConfigured && (
        <>
          <Flex justify="flex-start" align="center" gap="8px" className={styles.status}>
            <span className={isEnabled ? styles.statusDotEnabled : styles.statusDotDisabled} />
            <Text.Paragraph>
              {isEnabled
                ? 'SCIM provisioning is enabled. Your identity provider can create and manage users in this organization.'
                : 'SCIM provisioning is not enabled. Generate a token to let your identity provider manage users.'}
            </Text.Paragraph>
          </Flex>

          <Text.H5>SCIM Base URL</Text.H5>
          <CopyToClipboard text={scimBaseUrl}>
            <div className={styles.copyBox}>{scimBaseUrl}</div>
          </CopyToClipboard>

          <Flex justify="flex-start" gap="12px" className="margin-top-md">
            <Button
              type={BUTTON_TYPE.PRIMARY}
              isDisabled={isTokenMutationPending}
              onClick={() => generateToken()}
            >
              {isEnabled ? 'Regenerate Token' : 'Generate Token'}
            </Button>
            {isEnabled && (
              <Button
                type={BUTTON_TYPE.ERROR}
                isDisabled={isTokenMutationPending}
                onClick={() => revokeToken()}
              >
                Revoke Token
              </Button>
            )}
          </Flex>

          {generatedToken && (
            <div className={styles.newToken}>
              <Text.H5>Token successfully generated</Text.H5>
              <Text.Paragraph>Please copy this token. It will never be displayed again.</Text.Paragraph>
              <div className={styles.newTokenValue}>
                <TextInput
                  label="SCIM token"
                  value={generatedToken}
                  isReadOnly
                  suffix={<CopyToClipboard text={generatedToken} />}
                />
              </div>
            </div>
          )}
        </>
      )}

      <SupportedAttributes />
    </>
  );
};

export default ScimProvisioning;
